// Onda 5b — Tela /financeiro/medicoes.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Select, Button, Tag, Modal, Typography, Space, Card, Statistic,
  Empty, Row, Col, Alert, App as AntApp, Flex,
} from 'antd';
import {
  SearchOutlined, LinkOutlined, RocketOutlined, CheckCircleFilled, CopyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { pedirDesfazerLiberacao } from '../utils/desfazerLiberacao';
import { fmtDataBr as _fmtDataBr } from '../utils/dataFmt';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: any) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return 'R$ 0,00';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtData = _fmtDataBr;
const fmtDateTime = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('pt-BR') : '—';

interface ParcelaBm {
  id: string;
  contrato_id: string;
  contrato_numero: string;
  contrato_titulo: string;
  cliente_nome: string | null;
  descricao: string | null;
  parcela_titulo: string | null;
  tipo_parcela: string | null;
  valor_previsto: number;
  data_vencimento: string;
  status: string;
  bm_link_hash: string;
  bm_status: 'gerado' | 'assinado_cliente' | 'cancelado' | null;
  bm_gerado_em: string | null;
  bm_gerado_por: string | null;
  bm_assinado_em: string | null;
  bm_assinado_por_nome: string | null;
  liberada: boolean;
  entregaveis_vinculados: Array<{ id: string; descricao: string; status: string }>;
}

const TIPO_LABEL: Record<string, { label: string; color: string }> = {
  mobilizacao: { label: 'Mobilização', color: 'purple' },
  medicao:     { label: 'Medição',     color: 'processing' },
  saldo:       { label: 'Saldo',       color: 'success' },
  avulsa:      { label: 'Avulsa',      color: 'default' },
};

function FinanceiroMedicoesInner() {
  const { message, modal } = AntApp.useApp();
  const [items, setItems] = useState<ParcelaBm[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [linkBm, setLinkBm] = useState<{ url: string; nome: string } | null>(null);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_bms_em_fluxo' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setItems(Array.isArray(d) ? d : []);
    } finally { setLoading(false); }
  }

  function mostrarLink(p: ParcelaBm) {
    setLinkBm({
      url: `${window.location.origin}/publico/bm/${p.bm_link_hash}`,
      nome: p.descricao || p.parcela_titulo || '',
    });
  }

  function liberarManual(p: ParcelaBm) {
    modal.confirm({
      title: 'Liberar parcela sem aguardar assinatura?',
      content: `"${p.descricao}" — use isso quando o cliente já confirmou off-line.`,
      okText: 'Liberar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/propostas-geradas`, {
            action: 'fin_liberar_parcela', id: p.id,
          }, { headers: { Authorization: token } });
          message.success('Parcela liberada.');
          carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const filtrados = items.filter(p => {
    if (filtroStatus && p.bm_status !== filtroStatus) return false;
    if (busca) {
      const q = busca.toLowerCase();
      return (p.contrato_numero || '').toLowerCase().includes(q)
          || (p.cliente_nome || '').toLowerCase().includes(q)
          || (p.descricao || '').toLowerCase().includes(q)
          || (p.parcela_titulo || '').toLowerCase().includes(q);
    }
    return true;
  });

  const totalGerado = items.filter(p => p.bm_status === 'gerado')
    .reduce((s, p) => s + (p.valor_previsto || 0), 0);
  const totalAssinado = items.filter(p => (p.bm_status === 'assinado_cliente' && !p.liberada) || (p.bm_status === 'assinado_cliente' && p.liberada && p.status === 'pendente'))
    .reduce((s, p) => s + (p.valor_previsto || 0), 0);
  const qtdGerado = items.filter(p => p.bm_status === 'gerado').length;
  const qtdAssinado = items.filter(p => p.bm_status === 'assinado_cliente').length;

  const columns: ColumnsType<ParcelaBm> = [
    {
      title: 'Contrato', dataIndex: 'contrato_numero', key: 'c', width: 130,
      render: (v) => <Text style={{ fontFamily: 'monospace', color: '#020D7F' }}>{v}</Text>,
    },
    {
      title: 'Cliente', dataIndex: 'cliente_nome', key: 'cl',
      render: (v) => v || <Text type="secondary" italic>—</Text>,
    },
    {
      title: 'Parcela', key: 'p',
      render: (_, r) => (
        <div>
          <Text strong>{r.parcela_titulo || r.descricao}</Text>
          {r.entregaveis_vinculados && r.entregaveis_vinculados.length > 0 && (
            <Text style={{ display: 'block', fontSize: 10, color: '#888', marginTop: 2 }}>
              🔗 {r.entregaveis_vinculados.map(e => e.descricao).join(', ').slice(0, 80)}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Tipo', dataIndex: 'tipo_parcela', key: 't', width: 110, align: 'center',
      render: (v) => {
        const tp = v ? TIPO_LABEL[v] : null;
        return tp ? <Tag color={tp.color} style={{ margin: 0 }}>{tp.label}</Tag> : '—';
      },
    },
    {
      title: 'Valor', dataIndex: 'valor_previsto', key: 'v', width: 110, align: 'right',
      render: (v) => <Text strong>{fmt(v)}</Text>,
    },
    {
      title: 'BM', key: 'bm', width: 150, align: 'center',
      render: (_, p) => p.bm_status === 'assinado_cliente' ? (
        <div>
          <Tag color="success" style={{ margin: 0 }}>✓ Assinado</Tag>
          <div style={{ fontSize: 9, color: '#888', marginTop: 3 }}>
            por {p.bm_assinado_por_nome || '?'}
          </div>
          <div style={{ fontSize: 9, color: '#888' }}>
            {fmtDateTime(p.bm_assinado_em)}
          </div>
        </div>
      ) : (
        <div>
          <Tag color="purple" style={{ margin: 0 }}>📋 Gerado</Tag>
          <div style={{ fontSize: 9, color: '#888', marginTop: 3 }}>
            em {fmtData(p.bm_gerado_em)}
          </div>
        </div>
      ),
    },
    {
      title: 'Ação', key: 'a', width: 200, align: 'center',
      render: (_, p) => {
        const assinado = p.bm_status === 'assinado_cliente';
        return (
          <Space size={4}>
            <Button size="small" icon={<LinkOutlined />} onClick={() => mostrarLink(p)}
              title="Link público pro cliente">
              Link
            </Button>
            {!assinado && !p.liberada && (
              <Button size="small" type="default" icon={<RocketOutlined />}
                onClick={() => liberarManual(p)} title="Liberar manualmente">
                Liberar
              </Button>
            )}
            {p.liberada && p.status === 'pendente' && (
              <>
                <Text style={{ fontSize: 10, color: '#0a7f4a', fontWeight: 600 }}>
                  → emitir NF
                </Text>
                {/* Liberou por engano: desfaz E avisa quem emite (30/09/2026). */}
                <Button size="small" danger type="text" onClick={() => pedirDesfazerLiberacao({
                    modal, message, parcela: p as any, onFeito: carregar,
                  })} title="Desfazer a liberação e avisar quem emite a NF">
                  Desfazer
                </Button>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1320, margin: '0 auto' }}>
      <Flex align="baseline" gap={14} style={{ marginBottom: 18 }} wrap="wrap">
        <Title level={4} style={{ margin: 0, color: '#020D7F' }}>📐 Medições (BM)</Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {items.length} BM(s) em fluxo · {filtrados.length} visíveis
        </Text>
      </Flex>

      <Row gutter={14} style={{ marginBottom: 14 }}>
        <Col xs={24} md={12}>
          <Card size="small" style={{ borderColor: '#7C3AED55' }}>
            <Statistic
              title="📋 BMs gerados aguardando assinatura"
              value={totalGerado}
              precision={0}
              prefix="R$"
              valueStyle={{ color: '#7C3AED', fontSize: 20 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {qtdGerado} parcela(s) com link público enviado ao cliente
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" style={{ borderColor: '#0a7f4a55' }}>
            <Statistic
              title="✓ BMs assinados pelo cliente"
              value={totalAssinado}
              precision={0}
              prefix="R$"
              valueStyle={{ color: '#0a7f4a', fontSize: 20 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {qtdAssinado} parcela(s) liberadas pra emissão de NF
            </Text>
          </Card>
        </Col>
      </Row>

      <Flex gap={10} style={{ marginBottom: 14 }} wrap="wrap">
        <Input
          placeholder="Buscar contrato, cliente, descrição..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear
          style={{ flex: 1, minWidth: 240, maxWidth: 380 }}
        />
        <Select
          value={filtroStatus}
          onChange={setFiltroStatus}
          options={[
            { value: '', label: 'Todos status' },
            { value: 'gerado', label: '📋 Gerado (aguardando)' },
            { value: 'assinado_cliente', label: '✓ Assinado pelo cliente' },
          ]}
          style={{ width: 220 }}
        />
      </Flex>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtrados}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{
          emptyText: items.length === 0
            ? 'Nenhuma parcela com BM em fluxo. BMs são gerados quando contrato é iniciado ou operação marca um entregável como "Entregue".'
            : 'Nenhuma parcela corresponde ao filtro.'
        }}
        scroll={{ x: 'max-content' }}
      />

      <Alert
        type="info"
        showIcon={false}
        style={{ marginTop: 14 }}
        message={
          <Text style={{ fontSize: 11, lineHeight: 1.6 }}>
            <strong>📌 Fluxo:</strong> O BM é gerado quando
            <strong> (a)</strong> a operação marca um <em>entregável</em> como <strong>✓ Entregue</strong>
            (em Operacional → Projetos → Entregáveis), ou
            <strong> (b)</strong> uma <em>parcela tipo mobilização</em> é criada ao iniciar contrato.
            Aqui você acompanha os BMs gerados e pode reenviar o link, ou liberar manualmente
            sem aguardar a assinatura do cliente. Após assinatura/liberação, a parcela vira
            <strong style={{ color: '#7C3AED' }}> liberada</strong> em Contas a Receber pra
            <strong> 🧾 Emitir NF</strong>.
          </Text>
        }
      />

      {linkBm && (
        <ModalLinkBm link={linkBm} onClose={() => setLinkBm(null)} />
      )}
    </div>
  );
}

function ModalLinkBm({ link, onClose }: { link: { url: string; nome: string }; onClose: () => void }) {
  const { message } = AntApp.useApp();
  const [copiado, setCopiado] = useState(false);
  async function copiar() {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopiado(true);
      message.success('Link copiado.');
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      message.warning('Selecione e copie o link manualmente');
    }
  }
  return (
    <Modal
      open
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>Fechar</Button>,
        <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={copiar}>
          {copiado ? '✓ Link copiado' : 'Copiar link'}
        </Button>,
      ]}
      title={`📐 Link do BM — ${link.nome}`}
      width={580}
    >
      <Card size="small" style={{ background: '#FAFBFF', borderColor: '#0930D0', marginBottom: 14 }}>
        <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Link público pro cliente
        </Text>
        <div style={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all',
          background: '#fff', padding: 10, borderRadius: 4, border: '1px solid #ddd',
          color: '#020D7F', marginTop: 6 }}>
          {link.url}
        </div>
      </Card>
      <Text style={{ fontSize: 12, color: '#555', lineHeight: 1.5 }}>
        📤 <strong>Compartilhe esse link com o cliente</strong>. Ele abre, confere a parcela
        e assina digitalmente. Quando assinar, a parcela vira <strong style={{ color: '#7C3AED' }}>
        liberada</strong> em Contas a Receber, pronta pra emissão da NF.
      </Text>
    </Modal>
  );
}

export default function FinanceiroMedicoes() {
  return (
    <AntApp>
      <FinanceiroMedicoesInner />
    </AntApp>
  );
}
